import { useEffect, useRef, useState, useCallback } from 'react';

interface Crystal {
  id: number;
  x: number;
  size: number;
  duration: number;
  delay: number;
  opacity: number;
  rotationStart: number;
  rotationEnd: number;
  drift: number;
  color: string;
  shape: 'diamond' | 'hexagon' | 'star';
}

let crystalIdCounter = 0;

function getCrystalColor(): string {
  const colors = [
    'bg-white/40',
    'bg-secondary-200/50',
    'bg-secondary-300/40',
    'bg-paper-50/50',
    'bg-secondary-100/60',
    'bg-white/30',
    'bg-secondary-400/30',
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}

function getCrystalShape(): 'diamond' | 'hexagon' | 'star' {
  const shapes: Array<'diamond' | 'hexagon' | 'star'> = ['diamond', 'diamond', 'diamond', 'hexagon', 'star'];
  return shapes[Math.floor(Math.random() * shapes.length)];
}

export default function IceCrystalRain() {
  const [crystals, setCrystals] = useState<Crystal[]>([]);
  const scrollRef = useRef(0);
  const lastSpawnRef = useRef(0);
  const activeRef = useRef(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const spawnCrystals = useCallback((count: number, scrollY: number) => {
    const newCrystals: Crystal[] = [];
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;

    for (let i = 0; i < count; i++) {
      crystalIdCounter += 1;
      const size = 4 + Math.random() * 10;
      newCrystals.push({
        id: crystalIdCounter,
        x: Math.random() * viewportWidth,
        size,
        duration: 3 + Math.random() * 5,
        delay: Math.random() * 1.5,
        opacity: 0.15 + Math.random() * 0.35,
        rotationStart: Math.random() * 360,
        rotationEnd: (Math.random() - 0.5) * 360,
        drift: (Math.random() - 0.5) * 80,
        color: getCrystalColor(),
        shape: getCrystalShape(),
      });
    }

    setCrystals((prev) => {
      const combined = [...prev, ...newCrystals];
      // Keep max ~60 crystals for performance
      return combined.slice(-60);
    });

    // Remove crystals after they fall
    setTimeout(() => {
      if (!activeRef.current) return;
      setCrystals((prev) => prev.filter((c) => !newCrystals.find((nc) => nc.id === c.id)));
    }, 8000);
  }, []);

  useEffect(() => {
    activeRef.current = true;

    // Initial burst on mount
    spawnCrystals(18, 0);

    const handleScroll = () => {
      if (!activeRef.current) return;
      const scrollY = window.scrollY;
      const scrollDelta = scrollY - scrollRef.current;
      scrollRef.current = scrollY;

      const now = Date.now();
      // Spawn crystals based on scroll speed and time
      if (Math.abs(scrollDelta) > 2 && now - lastSpawnRef.current > 120) {
        const count = Math.min(Math.floor(Math.abs(scrollDelta) / 8) + 2, 8);
        spawnCrystals(count, scrollY);
        lastSpawnRef.current = now;
      }
    };

    // Also spawn crystals periodically even without scroll for ambient effect
    const ambientInterval = setInterval(() => {
      if (!activeRef.current) return;
      const scrollY = window.scrollY;
      // Only ambient spawn near top of page
      if (scrollY < 2000) {
        spawnCrystals(2, scrollY);
      }
    }, 2500);

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      activeRef.current = false;
      window.removeEventListener('scroll', handleScroll);
      clearInterval(ambientInterval);
    };
  }, [spawnCrystals]);

  const getShapeClass = (shape: Crystal['shape']) => {
    switch (shape) {
      case 'diamond':
        return 'rotate-45';
      case 'hexagon':
        return 'hex-crystal';
      case 'star':
        return 'star-crystal';
      default:
        return 'rotate-45';
    }
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 pointer-events-none z-[5] overflow-hidden"
      aria-hidden="true"
    >
      {crystals.map((crystal) => (
        <div
          key={crystal.id}
          className={`absolute ${getShapeClass(crystal.shape)} ${crystal.color}`}
          style={{
            left: `${crystal.x}px`,
            top: '-20px',
            width: `${crystal.size}px`,
            height: `${crystal.size}px`,
            opacity: crystal.opacity,
            animation: `crystalFall ${crystal.duration}s ${crystal.delay}s linear forwards, crystalRotate ${crystal.duration}s ${crystal.delay}s linear forwards`,
            '--crystal-drift': `${crystal.drift}px`,
            '--crystal-rotation-start': `${crystal.rotationStart}deg`,
            '--crystal-rotation-end': `${crystal.rotationEnd}deg`,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}